import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  DeletePanel,
  GetPanels
} from "../../../../api/diagnosis/master/panelAPI";

const ListPanel = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // =========================
  // Fetch Panels
  // =========================
  const fetchPanels = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPanels();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          departmentalSubSpeciality:
            item.departmentalSubSpecialityId?.name || "",
          rate: item.rate || 0,
          nightCharges: item.nightCharges || 0,
          discountAllow: item.discountAllow || "No",
          active: item.active || "Yes"
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", "Failed to fetch panels");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchPanels();
  }, []);

  // =========================
  // Search Filter
  // =========================
  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  // =========================
  // Handlers
  // =========================
  const handleEditClick = id => {
    navigate(`/diagnosis/master/panel/edit/${id}`);
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This panel will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePanel(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // =========================
  // Table Columns
  // =========================
  const columns = [
    { name: "S.No", selector: row => row.index, width: "10%" },
    {
      name: "Panel Name",
      selector: row => row.name,
      sortable: true,
      width: "20%"
    },
    {
      name: "Departmental Sub Speciality",
      selector: row => row.departmentalSubSpeciality,
      sortable: true,
      width: "20%"
    },
    { name: "Rate", selector: row => row.rate, sortable: true, width: "15%" },
    { name: "Night Charges", selector: row => row.nightCharges, width: "15%" },
    {
      name: "Discount Allowed",
      selector: row => row.discountAllow,
      width: "15%"
    },
    { name: "Active", selector: row => row.active, width: "10%" },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "15%"
    }
  ];

  // =========================
  // CSV Export
  // =========================
  const headers = [
    { label: "S.No", key: "index" },
    { label: "Panel Name", key: "name" },
    { label: "Departmental Sub Speciality", key: "departmentalSubSpeciality" },
    { label: "Rate", key: "rate" },
    { label: "Night Charges", key: "nightCharges" },
    { label: "Discount Allowed", key: "discountAllow" },
    { label: "Active", key: "active" }
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="panels.csv">
      <button className="btn btn-primary" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>;

  return (
    <Fragment>
      <Breadcrumb parent="Panel Master" title="List Panels" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>List Panels</h5>
              <Link to={`/diagnosis/master/panel/add`}>
                <button className="btn btn-primary" id="create">
                  Create Panel
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Panel..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPanel;
